import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import toast from '../components/Toast';
import { useSiteName } from '../platform/useSiteName';
import {
  Key,
  Copy,
  Check,
  Code,
  Terminal,
  FileJson,
  AlertCircle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  Sparkles,
  Video,
  List,
} from 'lucide-react';

// 获取网站 URL
function useSiteUrl() {
  const [siteUrl, setSiteUrl] = useState('');

  useEffect(() => {
    api.getSiteUrl().then((res) => {
      setSiteUrl(res.siteUrl || window.location.origin);
    }).catch(() => {
      setSiteUrl(window.location.origin);
    });
  }, []);

  return siteUrl;
}

type UserInfo = {
  id: string;
  email: string;
  name: string | null;
  apiKey: string;
  credits: number;
  role?: string;
}

const getCodeExamples = (baseUrl: string) => ({
  javascript: `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: "YOUR_API_KEY",
  baseURL: "${baseUrl}/api/v1",
  timeout: 600_000
});

// 文生图
const response = await client.images.generate({
  model: "__MODEL_ID__",
  prompt: "A beautiful sunset over mountains",
  size: "1024x1024",
  quality: "high",
  n: 1
});

const b64 = response.data[0].b64_json;
// 保存图片或显示在页面上`,

  python: `from openai import OpenAI
import base64

client = OpenAI(
    api_key="YOUR_API_KEY",
    base_url="${baseUrl}/api/v1",
    timeout=600
)

# 文生图
response = client.images.generate(
    model="__MODEL_ID__",
    prompt="A beautiful sunset over mountains",
    size="1024x1024",
    quality="high",
    n=1
)

b64 = response.data[0].b64_json
with open("output.png", "wb") as f:
    f.write(base64.b64decode(b64))`,

  curl: `curl -X POST ${baseUrl}/api/v1/images/generations \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "__MODEL_ID__",
    "prompt": "A beautiful sunset over mountains",
    "size": "1024x1024",
    "quality": "high",
    "n": 1
  }'`,
});

const getEditExamples = (baseUrl: string) => ({
  javascript: `// 图生图 / 局部重绘
const response = await client.images.edit({
  model: "__MODEL_ID__",
  image: fs.createReadStream("source.png"),
  mask: fs.createReadStream("mask.png"), // 可选，用于局部重绘
  prompt: "Change the background to a beach",
  size: "1024x1024",
  quality: "high"
});`,

  python: `# 图生图 / 局部重绘
with open("source.png", "rb") as img, open("mask.png", "rb") as msk:
    response = client.images.edit(
        model="__MODEL_ID__",
        image=img,
        mask=msk,  # 可选，用于局部重绘
        prompt="Change the background to a beach",
        size="1024x1024",
        quality="high"
    )
    
# 多图融合（具体上限取决于当前模型命中的渠道配置）
images = [
    open("character.jpg", "rb"),
    open("background.jpg", "rb"),
    open("outfit.jpg", "rb"),
]

response = client.images.edit(
    model="__MODEL_ID__",
    image=images,
    prompt="Place the character from image 1 into the background of image 2, wearing the style from image 3",
    size="2048x1152",
    quality="high"
)`,

  curl: `# 图生图需要 multipart/form-data
curl -X POST ${baseUrl}/api/v1/images/edits \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "model=__MODEL_ID__" \\
  -F "prompt=Change background to beach" \\
  -F "image=@source.png" \\
  -F "mask=@mask.png" \\
  -F "size=1024x1024" \\
  -F "quality=high"`,
});

const getModelsExamples = (baseUrl: string) => ({
  javascript: `// 列出全部可用模型
const list = await client.models.list();
for await (const model of list) {
  console.log(model.id);
}

// 查询单个模型
const model = await client.models.retrieve("gpt-image-2");
console.log(model.id, model.owned_by);`,

  python: `# 列出全部可用模型
models = client.models.list()
for model in models:
    print(model.id)

# 查询单个模型
model = client.models.retrieve("gpt-image-2")
print(model.id, model.owned_by)`,

  curl: `# 列出全部可用模型
curl ${baseUrl}/api/v1/models \\
  -H "Authorization: Bearer YOUR_API_KEY"

# 查询单个模型
curl ${baseUrl}/api/v1/models/gpt-image-2 \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
});

export default function ApiDocsPage() {
  const navigate = useNavigate();
  const siteName = useSiteName();
  const siteUrl = useSiteUrl();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [models, setModels] = useState<Awaited<ReturnType<typeof api.getModels>>['data']>([]);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeLang, setActiveLang] = useState<'javascript' | 'python' | 'curl'>('javascript');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['auth', 'models', 'generate']));

  useEffect(() => {
    const savedUser = api.getSavedUser();
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(savedUser);
  }, [navigate]);

  useEffect(() => {
    api.getModels().then((res) => setModels(res.data)).catch(() => {});
  }, []);

  const generateExampleModel = models.find((item) => item.enableText2img) || models[0];
  const editExampleModel = models.find((item) => item.enableImg2img) || models[0];
  const generateExampleModelId = generateExampleModel?.id || 'gpt-image-2';
  const editExampleModelId = editExampleModel?.id || generateExampleModelId;
  const generateCodeExamples = getCodeExamples(siteUrl || window.location.origin);
  const editCodeExamples = getEditExamples(siteUrl || window.location.origin);
  const modelsCodeExamples = getModelsExamples(siteUrl || window.location.origin);
  const resolvedGenerateExamples = {
    javascript: generateCodeExamples.javascript.replace(/__MODEL_ID__/g, generateExampleModelId),
    python: generateCodeExamples.python.replace(/__MODEL_ID__/g, generateExampleModelId),
    curl: generateCodeExamples.curl.replace(/__MODEL_ID__/g, generateExampleModelId),
  };
  const resolvedEditExamples = {
    javascript: editCodeExamples.javascript.replace(/__MODEL_ID__/g, editExampleModelId),
    python: editCodeExamples.python.replace(/__MODEL_ID__/g, editExampleModelId),
    curl: editCodeExamples.curl.replace(/__MODEL_ID__/g, editExampleModelId),
  };

  const handleRefreshApiKey = async () => {
    if (!confirm('确定要重新生成 API Key 吗？旧的 API Key 将立即失效。')) {
      return;
    }
    
    setRefreshing(true);
    try {
      const data = await api.refreshApiKey();
      setUser(prev => prev ? { ...prev, apiKey: data.apiKey } : null);
      toast.success('API Key 已重新生成');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '刷新失败');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const SectionHeader = ({ id, title, icon: Icon }: { id: string; title: string; icon: React.ElementType }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {expandedSections.has(id) ? (
        <ChevronUp className="w-5 h-5 text-gray-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  const CodeBlock = ({ code, language }: { code: string; language: string }) => (
    <div className="relative group">
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => handleCopy(code)}
          className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto text-sm leading-relaxed">
        <code>{code}</code>
      </pre>
      <div className="absolute left-2 top-2 px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
        {language}
      </div>
    </div>
  );

  const ParamTable = ({ params }: { params: { name: string; type: string; required: string; default?: string; description: string }[] }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4 font-semibold text-gray-700">参数</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">类型</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">必填</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">默认值</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">说明</th>
          </tr>
        </thead>
        <tbody>
          {params.map((param, idx) => (
            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 px-4 font-mono text-violet-600">{param.name}</td>
              <td className="py-3 px-4 text-gray-600">{param.type}</td>
              <td className="py-3 px-4">
                <span className={`px-2 py-1 rounded-full text-xs ${param.required === '是' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                  {param.required}
                </span>
              </td>
              <td className="py-3 px-4 text-gray-500">{param.default || '-'}</td>
              <td className="py-3 px-4 text-gray-600">{param.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-light-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="flex items-center gap-2 text-light-text hover:text-accent-blue transition-colors">
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">{siteName}</span>
              </button>
              <span className="text-light-muted">/</span>
              <span className="font-medium text-light-text">API 文档</span>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">OpenAI 兼容 API</h1>
          <p className="text-gray-600">
            使用标准的 OpenAI SDK 调用我们的图片生成服务
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate('/video-api-docs')}
              className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 transition-colors"
            >
              <Video className="w-4 h-4" />
              查看视频 API 文档
            </button>
          </div>
        </div>

        {/* API Key Section */}
        <div
          className="rounded-2xl p-6 mb-8 text-white"
          style={{ background: 'linear-gradient(90deg, #7c3aed 0%, #9333ea 100%)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6" />
              <h2 className="text-xl font-semibold">您的 API Key</h2>
            </div>
            <button
              onClick={handleRefreshApiKey}
              disabled={refreshing}
              className="p-2 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm">{refreshing ? '刷新中...' : '重新生成'}</span>
            </button>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between">
            <code className="font-mono text-sm break-all">{user.apiKey}</code>
            <button
              onClick={() => handleCopy(user.apiKey)}
              className="ml-4 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? '已复制' : '复制'}</span>
            </button>
          </div>
          <p className="mt-4 text-sm text-white/80">
            请妥善保管您的 API Key，不要分享给他人。当前剩余积分: {user.credits}
          </p>
        </div>

        {/* Quick Start */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Terminal className="w-5 h-5 text-violet-600" />
            快速开始
          </h2>
          
          {/* Language Selector */}
          <div className="flex gap-2 mb-4">
            {(['javascript', 'python', 'curl'] as const).map((lang) => (
              <button
                key={lang}
                onClick={() => setActiveLang(lang)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeLang === lang
                    ? 'bg-violet-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {lang === 'javascript' ? 'JavaScript' : lang === 'python' ? 'Python' : 'cURL'}
              </button>
            ))}
          </div>

          <CodeBlock 
            code={resolvedGenerateExamples[activeLang].replace('YOUR_API_KEY', user.apiKey)} 
            language={activeLang} 
          />
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-600" />
            当前模型能力
          </h2>
          <div className="space-y-4">
            {models.map((model) => (
              <div key={model.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="text-base font-semibold text-gray-900">{model.name}</h3>
                  <code className="rounded bg-gray-100 px-2 py-1 text-xs text-violet-700">{model.id}</code>
                </div>
                {model.description && (
                  <p className="text-sm text-gray-600 mb-3">{model.description}</p>
                )}
                <div className="grid gap-3 md:grid-cols-3">
                  {([
                    ['text2img', '文生图'],
                    ['img2img', '图生图'],
                    ['inpaint', '局部重绘'],
                  ] as const).map(([modeKey, label]) => {
                    const capability = model.modeCapabilities?.[modeKey];
                    return (
                      <div key={modeKey} className="rounded-lg bg-gray-50 p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-900">{label}</span>
                          <span className={`rounded-full px-2 py-1 text-xs ${
                            capability?.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {capability?.enabled ? '支持' : '不支持'}
                          </span>
                        </div>
                        {modeKey === 'img2img' && (
                          <div className="space-y-1 text-xs text-gray-600">
                            <p>参考图上限：{capability?.referenceImageMaxCount ?? '-'}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {models.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-200 p-6 text-sm text-gray-500">
                当前暂无可公开调用的模型，请到后台启用模型并配置可用渠道。
              </div>
            )}
          </div>
        </div>

        {/* Authentication */}
        <div className="space-y-4 mb-6">
          <SectionHeader id="auth" title="认证方式" icon={Key} />
          {expandedSections.has('auth') && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <p className="text-gray-600 mb-4">
                所有 API 请求都需要在 HTTP Header 中携带 API Key：
              </p>
              <CodeBlock 
                code={`Authorization: Bearer ${user.apiKey}`} 
                language="http" 
              />
            </div>
          )}
        </div>

        {/* Image Generation */}
        <div className="space-y-4 mb-6">
          <SectionHeader id="models" title="模型列表 /models" icon={List} />
          {expandedSections.has('models') && (
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <p className="text-gray-600">
                兼容 OpenAI <code className="font-mono text-violet-600">/v1/models</code> 接口，可列出当前可用的全部模型，或按模型 ID 查询单个模型。GUI 客户端（如 OpenWebUI、NextChat、LobeChat）可通过该接口自动拉取模型列表。
              </p>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <code className="font-mono text-sm text-violet-600 bg-white px-2 py-1 rounded">GET /api/v1/models</code>
                  <span className="text-sm text-gray-600">列出全部已启用模型</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <code className="font-mono text-sm text-violet-600 bg-white px-2 py-1 rounded">GET /api/v1/models/&#123;id&#125;</code>
                  <span className="text-sm text-gray-600">按模型 ID 查询单个模型</span>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">请求示例</h4>
                <CodeBlock code={modelsCodeExamples[activeLang].replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
              </div>

              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">列表响应示例</h4>
                <CodeBlock
                  code={`{
  "object": "list",
  "data": [
    {
      "id": "gpt-image-2",
      "object": "model",
      "created": 1779986618,
      "owned_by": "zhitu"
    }
  ]
}`}
                  language="json"
                />
              </div>

              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">单个模型响应示例</h4>
                <CodeBlock
                  code={`{
  "id": "gpt-image-2",
  "object": "model",
  "created": 1779986618,
  "owned_by": "zhitu"
}`}
                  language="json"
                />
              </div>

              <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">说明：</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>仅返回已启用（isActive）的模型</li>
                      <li><code className="font-mono">created</code> 为模型创建时间的 Unix 秒级时间戳</li>
                      <li><code className="font-mono">owned_by</code> 固定为 <code className="font-mono">zhitu</code></li>
                      <li>查询不存在的模型 ID 返回 404，错误体遵循 OpenAI 格式</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Image Generation */}
        <div className="space-y-4 mb-6">
          <SectionHeader id="generate" title="文生图 /images/generations" icon={Code} />
          {expandedSections.has('generate') && (
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <ParamTable
                params={[
                  { name: 'model', type: 'string', required: '是', default: generateExampleModelId, description: '模型 ID' },
                  { name: 'prompt', type: 'string', required: '是', description: '提示词，最多 32000 字符' },
                  { name: 'size', type: 'string', required: '否', default: 'auto', description: '图片尺寸，支持 8 种预设或自定义' },
                  { name: 'quality', type: 'string', required: '否', default: 'auto', description: '图片质量: low/medium/high/auto' },
                  { name: 'n', type: 'integer', required: '否', default: '1', description: '生成数量，只能等于1' },
                  { name: 'output_format', type: 'string', required: '否', default: 'png', description: '输出格式: png/jpeg/webp' },
                  { name: 'output_compression', type: 'integer', required: '否', default: '100', description: '压缩质量，1-100，仅 jpeg/webp 有效' },
                  { name: 'moderation', type: 'string', required: '否', default: 'auto', description: '审核级别: auto/low' },
                ]}
              />
              
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">请求示例</h4>
                <CodeBlock code={resolvedGenerateExamples[activeLang].replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
              </div>

              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">响应示例</h4>
                <CodeBlock
                  code={`{
  "created": 1699000000,
  "data": [
    {
      "b64_json": "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
      "revised_prompt": "A beautiful sunset over mountains with vibrant colors"
    }
  ]
}`}
                  language="json"
                />
              </div>
            </div>
          )}
        </div>

        {/* Image Edit */}
        <div className="space-y-4 mb-6">
          <SectionHeader id="edit" title="图生图 /images/edits" icon={FileJson} />
          {expandedSections.has('edit') && (
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <p className="text-gray-600">
                支持图片编辑、局部重绘（Inpainting）和多图融合。支持 multipart/form-data 格式上传图片文件。
              </p>
              
              <ParamTable
                params={[
                  { name: 'model', type: 'string', required: '是', default: editExampleModelId, description: '模型 ID' },
                  { name: 'image', type: 'file/array', required: '是', description: '参考图片，支持单张或多张；具体最大张数取决于当前模型命中的 API 渠道配置' },
                  { name: 'mask', type: 'file', required: '否', description: '蒙版图片，用于局部重绘（透明区域为要重绘部分）' },
                  { name: 'prompt', type: 'string', required: '是', description: '编辑提示词' },
                  { name: 'size', type: 'string', required: '否', default: 'auto', description: '输出尺寸' },
                  { name: 'quality', type: 'string', required: '否', default: 'auto', description: '图片质量' },
                  { name: 'n', type: 'integer', required: '否', default: '1', description: '生成数量，只能等于1' },
                  { name: 'output_format', type: 'string', required: '否', default: 'png', description: '输出格式' },
                ]}
              />

              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">请求示例</h4>
                <CodeBlock code={resolvedEditExamples[activeLang].replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
              </div>
            </div>
          )}
        </div>

        {/* Size Options */}
        <div className="space-y-4 mb-6">
          <SectionHeader id="sizes" title="尺寸选项" icon={BookOpen} />
          {expandedSections.has('sizes') && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { size: '1024x1024', desc: '1:1 标准方图' },
                  { size: '1536x1024', desc: '3:2 横版' },
                  { size: '1024x1536', desc: '2:3 竖版' },
                  { size: '2048x2048', desc: '2K 方图' },
                  { size: '2048x1152', desc: '16:9 横版（适合电脑壁纸/海报）' },
                  { size: '3840x2160', desc: '4K 横版' },
                  { size: '2160x3840', desc: '4K 竖版（适合手机壁纸）' },
                  { size: 'auto', desc: '由模型自动选择' },
                ].map((item) => (
                  <div key={item.size} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <code className="font-mono text-sm text-violet-600 bg-white px-2 py-1 rounded">{item.size}</code>
                    <span className="text-sm text-gray-600">{item.desc}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-4 bg-amber-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">自定义尺寸约束：</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>边长必须是 16 的整数倍</li>
                      <li>最大边长 ≤ 3840px</li>
                      <li>长短边比例 ≤ 3:1</li>
                      <li>总像素在 655,360 ~ 8,294,400 之间</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Handling */}
        <div className="space-y-4 mb-8">
          <SectionHeader id="errors" title="错误处理" icon={AlertCircle} />
          {expandedSections.has('errors') && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <p className="text-gray-600 mb-4">
                所有错误响应都遵循 OpenAI 风格的错误格式：
              </p>
              <CodeBlock
                code={`{
  "error": {
    "message": "错误描述信息",
    "type": "错误类型",
    "param": "相关参数（可选）",
    "code": "错误代码（可选）"
  }
}`}
                language="json"
              />
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                {[
                  { code: '401', type: 'invalid_api_key', desc: 'API Key 无效或缺失' },
                  { code: '400', type: 'invalid_request_error', desc: '请求参数错误' },
                  { code: '429', type: 'insufficient_quota', desc: '积分不足' },
                  { code: '500', type: 'api_error', desc: '服务器内部错误' },
                ].map((item) => (
                  <div key={item.code} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-mono">{item.code}</span>
                    <code className="text-sm text-violet-600">{item.type}</code>
                    <span className="text-sm text-gray-600">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Links */}
        <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
          <a href="/faq" className="flex items-center gap-1 hover:text-violet-600 transition-colors">
            <BookOpen className="w-4 h-4" />
            常见问题
          </a>
          <a href="/" className="flex items-center gap-1 hover:text-violet-600 transition-colors">
            <ExternalLink className="w-4 h-4" />
            返回首页
          </a>
        </div>
      </main>
    </div>
  );
}
